export type NavItem = { label: string; active?: boolean; hot?: boolean };

export type HeroFeature = {
  tag: string;
  title: string;
  excerpt: string;
  image: string;
  meta: string;
  views: string;
  stages: string[];
};

export type HeroSide = {
  tag: string;
  title: string;
  image: string;
  meta: string;
  views: string;
};

export type HeroBrief = {
  rank: number;
  title: string;
  source: string;
  heat: string;
  trend: 'up' | 'flat' | 'down';
};

export type TickerItem = { text: string; heat: string };

export const navItems: NavItem[] = [
  { label: '首页', active: true },
  { label: '事件始末' },
  { label: '时间线', hot: true },
  { label: '全过程梳理' },
  { label: '深度解析' },
  { label: '热度榜', hot: true },
];

export const tickerItems: TickerItem[] = [
  { text: '某头部主播回应传闻：完整时间线已整理完毕', heat: '热度 89.2万' },
  { text: '某品牌代言争议事件始末，从起因到回应一次看清', heat: '热度 74.5万' },
  { text: '短剧圈合作纠纷全过程梳理，关键节点全部标出', heat: '热度 61.8万' },
  { text: '音乐节临时取消始末：主办方与艺人各自回应', heat: '热度 55.3万' },
  { text: '直播切片争议来龙去脉，网友整理出完整证据链', heat: '热度 48.1万' },
];

export const heroFeature: HeroFeature = {
  tag: '本周核心',
  title: '某头部主播争议事件始末：从一句口误到全网发酵，全过程梳理',
  excerpt:
    '围绕本次事件，我们按时间顺序整理了起因、发酵、二次回应与后续处理四个阶段。哪些节点真正推动了舆论转向，哪些说法仍缺少原始出处，一文看清前因后果。',
  image:
    'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photograph%20of%20an%20empty%20conference%20table%20with%20warm%20amber%20rim%20light%2C%20deep%20shadows%2C%20blurred%20night%20city%20bokeh%20in%20background%2C%20film%20grain%2C%20high%20contrast%2C%20atmospheric%2C%20no%20text&width=1200&height=800&seq=hl-hero-main&orientation=landscape',
  meta: '专题组 · 8 分钟阅读',
  views: '阅读 128.6万',
  stages: ['起因', '发酵', '回应', '后续'],
};

export const heroSideCards: HeroSide[] = [
  {
    tag: '时间线',
    title: '48 小时关键节点：谁在什么时候说了什么',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20vintage%20wall%20clock%20in%20low%20key%20lighting%2C%20warm%20amber%20highlight%2C%20deep%20shadow%2C%20blurred%20urban%20night%20bokeh%2C%20film%20grain%2C%20dramatic%20atmosphere%2C%20no%20text&width=640&height=440&seq=hl-side-01&orientation=landscape',
    meta: '时间线 · 5 分钟',
    views: '63.4万',
  },
  {
    tag: '深度解析',
    title: '争议为什么突然转向？三点被忽略的传播逻辑',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20hand%20holding%20a%20glowing%20phone%20in%20the%20dark%2C%20warm%20amber%20screen%20glow%2C%20deep%20shadows%2C%20blurred%20city%20lights%20bokeh%2C%20film%20grain%2C%20dramatic%2C%20no%20text&width=640&height=440&seq=hl-side-02&orientation=landscape',
    meta: '解析 · 6 分钟',
    views: '41.7万',
  },
];

export const heroBriefs: HeroBrief[] = [
  { rank: 1, title: '代言争议事件：品牌方首次给出正式回应', source: '商业观察', heat: '74.5万', trend: 'up' },
  { rank: 2, title: '短剧合作纠纷全过程，双方各执一词', source: '短剧前线', heat: '61.8万', trend: 'up' },
  { rank: 3, title: '音乐节取消始末：退票方案与后续安排', source: '现场记录', heat: '55.3万', trend: 'flat' },
  { rank: 4, title: '直播切片争议来龙去脉，证据链整理', source: '直播观察', heat: '48.1万', trend: 'down' },
];

export type RankRow = {
  rank: number;
  title: string;
  angle: string;
  source: string;
  heat: number;
  delta: string;
  trend: 'up' | 'flat' | 'down';
};

export const rankTabs = ['总榜', '事件始末', '时间线', '深度解析'] as const;

export const rankData: Record<string, RankRow[]> = {
  总榜: [
    { rank: 1, title: '某头部主播口误风波', angle: '事件始末全记录', source: '专题组', heat: 892, delta: '+18%', trend: 'up' },
    { rank: 2, title: '品牌代言争议事件', angle: '品牌方回应梳理', source: '商业观察', heat: 745, delta: '+12%', trend: 'up' },
    { rank: 3, title: '短剧圈合作纠纷', angle: '时间线逐条对照', source: '短剧前线', heat: 618, delta: '+6%', trend: 'up' },
    { rank: 4, title: '音乐节临时取消', angle: '始末与退票后续', source: '现场记录', heat: 553, delta: '持平', trend: 'flat' },
    { rank: 5, title: '直播切片争议', angle: '来龙去脉复盘', source: '直播观察', heat: 481, delta: '-4%', trend: 'down' },
    { rank: 6, title: '综艺片段二次剪辑', angle: '前后语境还原', source: '综艺笔记', heat: 402, delta: '+9%', trend: 'up' },
    { rank: 7, title: '联名款下架风波', angle: '全过程节点梳理', source: '消费观察', heat: 366, delta: '持平', trend: 'flat' },
    { rank: 8, title: '粉丝应援资金争议', angle: '来龙去脉与回应', source: '饭圈观察', heat: 318, delta: '-7%', trend: 'down' },
  ],
  事件始末: [
    { rank: 1, title: '从一条动态说起：事件最初的触发点', angle: '起因阶段还原', source: '专题组', heat: 812, delta: '+21%', trend: 'up' },
    { rank: 2, title: '舆情第一次反转出现在何时', angle: '发酵阶段节点', source: '时间线组', heat: 690, delta: '+14%', trend: 'up' },
    { rank: 3, title: '当事方回应逐句拆解', angle: '回应阶段梳理', source: '深度组', heat: 574, delta: '+5%', trend: 'up' },
    { rank: 4, title: '事件如何收尾：后续处理清单', angle: '后续阶段汇总', source: '专题组', heat: 455, delta: '持平', trend: 'flat' },
  ],
  时间线: [
    { rank: 1, title: '第 1 小时：原始内容首次出现', angle: '时间线起点', source: '时间线组', heat: 901, delta: '+26%', trend: 'up' },
    { rank: 2, title: '第 6 小时：首个大号转发', angle: '扩散节点', source: '时间线组', heat: 733, delta: '+11%', trend: 'up' },
    { rank: 3, title: '第 18 小时：当事方首次回应', angle: '关键转折', source: '时间线组', heat: 655, delta: '+8%', trend: 'up' },
    { rank: 4, title: '第 48 小时：话题热度回落', angle: '收尾节点', source: '数据组', heat: 421, delta: '-6%', trend: 'down' },
  ],
  深度解析: [
    { rank: 1, title: '情绪如何被点燃：传播链的三个拐点', angle: '传播机制解析', source: '深度组', heat: 764, delta: '+16%', trend: 'up' },
    { rank: 2, title: '信息真假混杂时，如何自建核实清单', angle: '信息核实方法', source: '深度组', heat: 612, delta: '+9%', trend: 'up' },
    { rank: 3, title: '为什么回应反而放大了争议', angle: '回应策略解析', source: '深度组', heat: 528, delta: '持平', trend: 'flat' },
    { rank: 4, title: '同类事件复盘的共通规律', angle: '横向对照解析', source: '深度组', heat: 447, delta: '+4%', trend: 'up' },
  ],
};

export type FeedCard = {
  tag: string;
  title: string;
  excerpt: string;
  image: string;
  source: string;
  time: string;
  views: string;
  comments: string;
  badge?: string;
};

export const feedCards: FeedCard[] = [
  {
    tag: '事件始末',
    title: '代言争议事件始末：品牌与当事人各自的时间说法对照',
    excerpt: '把双方公开说明按时间排开，争议点集中在第一条内容的发布时间与授权范围。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20luxury%20brand%20storefront%20at%20night%20with%20warm%20amber%20window%20glow%2C%20deep%20shadows%2C%20wet%20street%20reflections%2C%20film%20grain%2C%20dramatic%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-01&orientation=landscape',
    source: '商业观察',
    time: '2 小时前',
    views: '38.2万',
    comments: '1.2万',
    badge: '进行中',
  },
  {
    tag: '时间线',
    title: '48 小时逐条记录：从第一条动态到当事方回应',
    excerpt: '按小时标注每一个可核实的节点，标出哪些说法目前仍只有单方信源。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20timeline%20wall%20with%20warm%20amber%20pinned%20notes%20and%20string%2C%20low%20key%20lighting%2C%20deep%20shadows%2C%20film%20grain%2C%20mysterious%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-02&orientation=landscape',
    source: '时间线组',
    time: '4 小时前',
    views: '29.7万',
    comments: '8600',
  },
  {
    tag: '全过程',
    title: '短剧合作纠纷全过程梳理：合同、分成与沟通记录',
    excerpt: '从项目启动到公开争执，四个阶段的关键证据被重新整理成一页。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20scattered%20film%20scripts%20and%20clapperboard%20under%20warm%20amber%20desk%20lamp%2C%20low%20key%20lighting%2C%20deep%20shadows%2C%20film%20grain%2C%20dramatic%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-03&orientation=landscape',
    source: '短剧前线',
    time: '6 小时前',
    views: '22.4万',
    comments: '6300',
  },
  {
    tag: '来龙去脉',
    title: '音乐节取消始末：天气、审批与后续退票安排',
    excerpt: '把主办方公告与现场记录并排看，取消决定的完整链条逐渐清晰。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20an%20empty%20outdoor%20music%20festival%20stage%20at%20night%20with%20warm%20amber%20lights%2C%20low%20key%2C%20deep%20shadows%2C%20light%20rain%2C%20film%20grain%2C%20dramatic%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-04&orientation=landscape',
    source: '现场记录',
    time: '9 小时前',
    views: '18.9万',
    comments: '4100',
  },
  {
    tag: '深度解析',
    title: '回应为什么没平息争议：三条被忽略的表达逻辑',
    excerpt: '对照三段公开说明，拆解措辞变化背后可能存在的传播误读。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20podcast%20microphone%20in%20a%20dark%20studio%20with%20warm%20amber%20accent%20light%2C%20low%20key%2C%20deep%20shadows%2C%20film%20grain%2C%20moody%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-05&orientation=landscape',
    source: '深度组',
    time: '12 小时前',
    views: '15.6万',
    comments: '3400',
  },
  {
    tag: '热度追踪',
    title: '本周热度趋势榜：哪些事件在持续升温',
    excerpt: '以讨论量、转发量与关注曲线为依据，整理本周仍在发酵的事件。',
    image:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20glowing%20data%20charts%20on%20a%20dark%20screen%20with%20warm%20amber%20curves%2C%20low%20key%20lighting%2C%20deep%20shadows%2C%20film%20grain%2C%20futuristic%20atmosphere%2C%20no%20text&width=800&height=600&seq=hl-feed-06&orientation=landscape',
    source: '数据组',
    time: '昨天',
    views: '12.1万',
    comments: '2100',
  },
];

export type TimelineNode = {
  time: string;
  stage: string;
  title: string;
  desc: string;
  tone: 'primary' | 'accent' | 'secondary';
};

export const timelineNodes: TimelineNode[] = [
  {
    time: '09-10 21:40',
    stage: '起因',
    title: '原始内容首次出现',
    desc: '一段直播片段被截取上传，讨论量在两个平台同步上升。',
    tone: 'primary',
  },
  {
    time: '09-11 02:15',
    stage: '发酵',
    title: '首个大号转发并补充背景',
    desc: '转发附加了往期相关内容，事件从单一片段扩展为系列讨论。',
    tone: 'accent',
  },
  {
    time: '09-11 14:30',
    stage: '转折',
    title: '当事方发布第一条说明',
    desc: '说明中公布了部分沟通记录，舆论焦点从事实转向态度。',
    tone: 'secondary',
  },
  {
    time: '09-11 23:05',
    stage: '二次发酵',
    title: '第三方介入发布完整录音',
    desc: '新素材出现后，此前若干说法需要重新核对时间顺序。',
    tone: 'primary',
  },
  {
    time: '09-12 10:00',
    stage: '回应',
    title: '双方同步给出书面回应',
    desc: '两份回应在关键节点上表述接近，但授权范围说法仍有差异。',
    tone: 'accent',
  },
  {
    time: '09-12 18:20',
    stage: '后续',
    title: '相关合作方调整公开物料',
    desc: '话题热度开始回落，讨论转向后续处理与行业惯例。',
    tone: 'secondary',
  },
];

export type DeepArticle = {
  chapter: string;
  title: string;
  excerpt: string;
  words: string;
  thumb: string;
};

export const deepArticles: DeepArticle[] = [
  {
    chapter: '解析 01',
    title: '传播链复盘：情绪是如何被一步步点燃的',
    excerpt: '从初始片段到大范围讨论，中间至少经历三次语境变化，每一次都改变了事件的解释框架。',
    words: '3200 字',
    thumb:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20chain%20of%20warm%20amber%20light%20bulbs%20in%20deep%20darkness%2C%20low%20key%20lighting%2C%20film%20grain%2C%20dramatic%20atmosphere%2C%20no%20text&width=480&height=360&seq=hl-deep-01&orientation=landscape',
  },
  {
    chapter: '解析 02',
    title: '信息混杂时，普通人可以怎么核实',
    excerpt: '给出一套可执行的核实清单：先看原始出处，再看时间戳，最后对照多方表述。',
    words: '2600 字',
    thumb:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20magnifying%20glass%20over%20old%20newspapers%20with%20warm%20amber%20lamp%20light%2C%20low%20key%2C%20deep%20shadows%2C%20film%20grain%2C%20no%20text&width=480&height=360&seq=hl-deep-02&orientation=landscape',
  },
  {
    chapter: '解析 03',
    title: '回应策略对照：为什么有的说明反而放大争议',
    excerpt: '把三类常见回应方式放在一起比较，观察措辞、节奏与信源透明度带来的不同结果。',
    words: '2900 字',
    thumb:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20two%20microphones%20facing%20each%20other%20on%20a%20dark%20table%20with%20warm%20amber%20rim%20light%2C%20low%20key%2C%20deep%20shadows%2C%20film%20grain%2C%20no%20text&width=480&height=360&seq=hl-deep-03&orientation=landscape',
  },
  {
    chapter: '解析 04',
    title: '同类事件横向对照：几个反复出现的共性',
    excerpt: '挑选近半年可查证的相似案例，归纳出三条反复出现的传播与收尾规律。',
    words: '3400 字',
    thumb:
      'https://readdy.ai/api/search-image?query=dark%20moody%20cinematic%20editorial%20photo%20of%20a%20stack%20of%20dark%20archive%20folders%20and%20notes%20under%20warm%20amber%20light%2C%20low%20key%2C%20deep%20shadows%2C%20film%20grain%2C%20no%20text&width=480&height=360&seq=hl-deep-04&orientation=landscape',
  },
];

export type ProcessStage = {
  step: string;
  name: string;
  desc: string;
  keyPoint: string;
};

export const processStages: ProcessStage[] = [
  { step: '01', name: '起因', desc: '原始素材出现，争议点尚未被明确定义。', keyPoint: '确认最初出处' },
  { step: '02', name: '发酵', desc: '转发与二次解读叠加，讨论范围迅速扩大。', keyPoint: '区分事实与解读' },
  { step: '03', name: '回应', desc: '当事方公开说明，焦点转向说法是否一致。', keyPoint: '核对时间戳' },
  { step: '04', name: '后续', desc: '热度回落，讨论转向处理方式与行业惯例。', keyPoint: '关注收尾动作' },
];

export type TopicItem = {
  name: string;
  icon: string;
  count: string;
  desc: string;
  accent: 'primary' | 'accent' | 'secondary';
};

export const topicItems: TopicItem[] = [
  { name: '娱乐动态', icon: 'ri-movie-2-line', count: '218 个事件', desc: '片场、合作与公开活动的相关讨论整理', accent: 'primary' },
  { name: '网红主播', icon: 'ri-live-line', count: '176 个事件', desc: '直播切片与账号动态的时间线归档', accent: 'accent' },
  { name: '品牌商业', icon: 'ri-store-3-line', count: '134 个事件', desc: '代言、联名与合作的始末梳理', accent: 'secondary' },
  { name: '文娱演出', icon: 'ri-mic-line', count: '98 个事件', desc: '音乐节、演唱会与临时调整记录', accent: 'primary' },
  { name: '综艺话题', icon: 'ri-tv-2-line', count: '86 个事件', desc: '剪辑争议与前后语境还原', accent: 'accent' },
  { name: '社交平台', icon: 'ri-chat-3-line', count: '152 个事件', desc: '跨平台讨论走向与热度曲线', accent: 'secondary' },
];

export type BuzzItem = {
  id: number;
  user: string;
  avatarColor: 'primary' | 'accent' | 'secondary';
  content: string;
  likes: string;
  topic: string;
  time: string;
};

export const buzzItems: BuzzItem[] = [
  {
    id: 1,
    user: '晚风记录簿',
    avatarColor: 'primary',
    content: '终于有人把时间线排清楚了，之前刷到十几条零散帖子越看越乱，这份梳理直接对着时间戳看就明白。',
    likes: '2.4万',
    topic: '时间线整理',
    time: '1 小时前',
  },
  {
    id: 2,
    user: '只看证据的人',
    avatarColor: 'accent',
    content: '建议置顶那段关于"区分事实和解读"的说明，很多争论其实卡在这一步。',
    likes: '1.8万',
    topic: '来龙去脉',
    time: '3 小时前',
  },
  {
    id: 3,
    user: '午后翻页',
    avatarColor: 'secondary',
    content: '从起因到后续四个阶段分开看确实清楚多了，省得自己一条条去翻评论区。',
    likes: '1.1万',
    topic: '全过程',
    time: '5 小时前',
  },
  {
    id: 4,
    user: '数据搬运工',
    avatarColor: 'accent',
    content: '热度曲线那张图挺有用，能看出来第二次发酵的节点和第一家媒体介入时间基本吻合。',
    likes: '8600',
    topic: '热度追踪',
    time: '8 小时前',
  },
];

export const siteStats = [
  { label: '已归档事件', value: '1,246' },
  { label: '时间线节点', value: '9,830' },
  { label: '深度解析', value: '412' },
  { label: '持续更新', value: '每日' },
];